import { useMemo, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAccuracy,
  calculateAverageReactionTime,
  calculateTotalTime,
} from "../shared/gameMetrics";

import "./MissingNumber.css";

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    maxNumber: 5,
    optionCount: 4,
    columns: 2,
  },

  medium: {
    label: "Media",
    totalRounds: 10,
    maxNumber: 7,
    optionCount: 4,
    columns: 3,
  },

  hard: {
    label: "Difícil",
    totalRounds: 12,
    maxNumber: 10,
    optionCount: 5,
    columns: 3,
  },
};

function randomNumber(min, max) {
  return Math.floor(
    Math.random() * (max - min + 1)
  ) + min;
}

function shuffle(list) {
  const copy = [...list];

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1)
    );

    [copy[index], copy[randomIndex]] = [
      copy[randomIndex],
      copy[index],
    ];
  }

  return copy;
}

function createOptions(correctAnswer, maxNumber, optionCount) {
  const options = new Set([correctAnswer]);

  while (options.size < optionCount) {
    options.add(randomNumber(1, maxNumber));
  }

  return shuffle([...options]);
}

function createRound(config) {
  const completeSequence = Array.from(
    { length: config.maxNumber },
    (_, index) => index + 1
  );

  const missingNumber = randomNumber(
    1,
    config.maxNumber
  );

  const visibleNumbers = shuffle(
    completeSequence.filter(
      (number) => number !== missingNumber
    )
  );

  const options = createOptions(
    missingNumber,
    config.maxNumber,
    Math.min(
      config.optionCount,
      config.maxNumber
    )
  );

  return {
    completeSequence,
    missingNumber,
    visibleNumbers,
    options,
  };
}

export default function MissingNumber() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correctAnswers, setCorrectAnswers] =
    useState(0);

  const [errors, setErrors] = useState(0);

  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () =>
      calculateProgress(
        roundIndex,
        totalRounds
      ),
    [roundIndex, totalRounds]
  );

  const totalAnswers = correctAnswers + errors;

  const accuracy = calculateAccuracy(
    correctAnswers,
    totalAnswers
  );

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (
    difficultyOrConfig = selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: nextConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(createRound(nextConfig));

    setCorrectAnswers(0);
    setErrors(0);
    setResponses([]);

    setStarted(true);
    setFinished(false);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
  };

  const answer = (selectedNumber) => {
    if (
      !started ||
      finished ||
      !round ||
      !config
    ) {
      return;
    }

    const now = Date.now();

    const reactionTime =
      now - roundStartTime;

    const isCorrect =
      selectedNumber === round.missingNumber;

    setResponses((previous) => [
      ...previous,
      {
        round: roundIndex + 1,
        selectedNumber,
        correctAnswer: round.missingNumber,
        maxNumber: config.maxNumber,
        isCorrect,
        reactionTime,
      },
    ]);

    if (isCorrect) {
      setCorrectAnswers(
        (previous) => previous + 1
      );
    } else {
      setErrors((previous) => previous + 1);
    }

    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= config.totalRounds) {
      setStarted(false);
      setFinished(true);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRoundIndex);
    setRound(createRound(config));
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);

    setRoundIndex(0);
    setRound(null);

    setStarted(false);
    setFinished(false);

    setCorrectAnswers(0);
    setErrors(0);
    setResponses([]);

    setStartTime(null);
    setEndTime(null);
    setRoundStartTime(null);
  };

  if (
    !selectedDifficulty &&
    !started &&
    !finished
  ) {
    return (
      <GameLauncher
        title="¿Qué número falta?"
        description="Observa los números mostrados e identifica cuál falta en el conjunto."
        areas={[
          "Atención sostenida",
          "Atención selectiva",
          "Rastreo visual",
          "Secuenciación numérica",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            max: 30,
            defaultValue: 10,
          }),

          {
            name: "maxNumber",
            label: "Número máximo de la serie",
            type: "number",
            min: 3,
            max: 30,
            defaultValue: 10,
            required: true,
            help:
              "Ejemplo: con 10 se utilizarán los números del 1 al 10.",
          },

          {
            name: "optionCount",
            label: "Cantidad de respuestas posibles",
            type: "number",
            min: 2,
            max: 10,
            defaultValue: 5,
            required: true,
            validate: (value, values) => {
              if (
                Number(value) >
                Number(values.maxNumber)
              ) {
                return "La cantidad de respuestas no puede ser mayor que el número máximo de la serie.";
              }

              return "";
            },
          },

          GameFields.columns({
            min: 2,
            max: 6,
            defaultValue: 3,
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  return (
    <div className="missing-number-page">
      <div className="missing-number-card">
        <header className="missing-number-header">
          <div>
            <h1>¿Qué número falta?</h1>

            <p>
              Dificultad:{" "}
              <strong>{config?.label}</strong>
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              startGame(selectedDifficulty)
            }
          >
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="number-reference-section">
              <span>SERIE COMPLETA</span>

              <div className="number-reference-list">
                {round.completeSequence.map(
                  (number) => (
                    <div
                      key={number}
                      className="number-reference-item"
                    >
                      {number}
                    </div>
                  )
                )}
              </div>
            </section>

            <section className="missing-number-instruction">
              <h2>¿Qué número falta?</h2>

              <p>
                Observa los números del conjunto y
                encuentra cuál no aparece.
              </p>
            </section>

            <section
              className="missing-number-grid"
              style={{
                gridTemplateColumns: `repeat(${config.columns}, 1fr)`,
              }}
            >
              {round.visibleNumbers.map(
                (number, index) => (
                  <div
                    key={`${number}-${index}`}
                    className="visible-number-item"
                  >
                    {number}
                  </div>
                )
              )}
            </section>

            <section className="missing-number-options">
              {round.options.map((number) => (
                <button
                  key={number}
                  type="button"
                  onClick={() => answer(number)}
                >
                  {number}
                </button>
              ))}
            </section>

            <section className="missing-number-score">
              <span>
                Aciertos: {correctAnswers}
              </span>

              <span>
                Errores: {errors}
              </span>

              <span>
                Precisión: {accuracy}%
              </span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                {
                  label: "Aciertos",
                  value: correctAnswers,
                },
                {
                  label: "Errores",
                  value: errors,
                },
                {
                  label: "Precisión",
                  value: `${accuracy}%`,
                },
                {
                  label: "Tiempo medio",
                  value: `${averageReactionTime} ms`,
                },
                {
                  label: "Tiempo total",
                  value: `${totalTime}s`,
                },
              ]}
              onRepeat={() =>
                startGame(selectedDifficulty)
              }
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation
              interpretation={interpretation}
            />
          </>
        )}
      </div>
    </div>
  );
}